import { Close, Delete, DragIndicator, Save } from '@mui/icons-material';
import { FormGroup, IconButton, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import type { IOrgUnitSalesView } from '@sharedInterfaces/IOrgUnit';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import editOUSalesViews from '@src/APIs/graphQl/OrgUnit/editOUSalesViews';
import Button from '@src/App/Components/Buttons/Button/Button';
import Dialog from '@src/App/Components/Dialogs/Dialog/Dialog';
import { useErrorDialog } from '@src/App/Components/Dialogs/ErrorDialog/ErrorDialog';
import type { AppState } from '@store/store';
import React, { useState } from 'react';
import { DragDropContext, Draggable, Droppable, type OnDragEndResponder } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';

interface SalesContentViewSettingsProps {
    id: string;
    views: IOrgUnitSalesView[];
    resolve: (result: IOrgUnitSalesView[]) => void;
}

export const SalesContentViewSettings: React.FC<SalesContentViewSettingsProps> = ({ id, views, resolve }) => {
    const showErrorDialog = useErrorDialog();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).orgUnit;

    const [isOpen, setIsOpen] = useState(true);
    const [stateViews, setStateViews] = useState<IOrgUnitSalesView[]>(views);
    const [saving, setSaving] = useState(false);

    const onConfirm = () => {
        setSaving(true);
        editOUSalesViews(stateViews)
            .then(() => {
                setIsOpen(false);
                resolve(stateViews);
            })
            .catch((ex) => {
                showErrorDialog(ex);
                setSaving(false);
            });
    };
    const onClose = () => {
        resolve(views);
        setIsOpen(false);
    };

    const onDragEnd: OnDragEndResponder = (result) => {
        const { source, destination } = result;
        if (!destination) {
            return;
        }
        if (source.droppableId === destination.droppableId && source.index === destination.index) {
            return;
        }
        const newStateViews = Array.from(stateViews);
        const [reorderedItem] = newStateViews.splice(source.index, 1);
        newStateViews.splice(destination.index, 0, reorderedItem);
        setStateViews(newStateViews);
    };
    const handleRemoveView = (viewId: number) => {
        const filteredViews = stateViews.filter((view) => view.id !== viewId);
        setStateViews(filteredViews);
    };

    if (!isOpen) return null;

    return (
        <Dialog
            id={id}
            footer={
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <Button text={langStrings.oftenUsed.close} disabled={saving} icon={<Close />} onClick={onClose} />
                    <Button
                        text={langStrings.oftenUsed.confirm}
                        disabled={saving}
                        icon={<Save />}
                        onClick={onConfirm}
                    />
                </div>
            }
            title={langStrings.salesViewDialogTitle}
            onClose={onClose}
        >
            <div style={{ margin: 10 }}>
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="droppable-fields">
                        {(provided) => (
                            <FormGroup
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                style={{
                                    overflow: 'hidden', //Fix für D&D
                                }}
                            >
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid>
                                        <div style={{ width: 22 }} />
                                    </Grid>
                                    <Grid size="grow">
                                        <Typography variant="caption">{langStrings.view}</Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography variant="caption">{langStrings.oftenUsed.delete}</Typography>
                                    </Grid>
                                </Grid>
                                {stateViews.map((view, index) => (
                                    <Draggable key={view.id} draggableId={view.id.toString()} index={index}>
                                        {(provided) => (
                                            <Grid
                                                container
                                                alignItems="center"
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                spacing={1}
                                            >
                                                <Grid>
                                                    <DragIndicator />
                                                </Grid>
                                                <Grid size="grow">{view.title}</Grid>
                                                <Grid>
                                                    <IconButton onClick={() => handleRemoveView(view.id)}>
                                                        <Delete />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </FormGroup>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
            {/* <pre>
                {JSON.stringify(settings, null, 2)}
            </pre>
            <pre>
                {JSON.stringify(fields, null, 2)}
                {JSON.stringify(sortedFields, null, 2)}
            </pre> */}
        </Dialog>
    );
};
